import {
  AccordionData,
  BlogData,
  BlogFM,
  BulletsData,
  ButtonData,
  CalendarData,
  ContactFormData,
  ContentType,
  DocumentData,
  DSolutionsBadgeData,
  FoodMenuData,
  FormData,
  GalleryData,
  HeaderData,
  ImageCardsData,
  ImageCollageData,
  ImageData,
  LogoSectionData,
  OpeningHoursData,
  QuicklinksData,
  RichTextData,
  ServiceCardData,
  SponsorsData,
  TipsBoxData,
  YtVideoData,
} from '@bm-js/atlascloud-shared'
import {
  BulletPoints,
  ButtonContent,
  Gallery,
  HeaderContent,
  ImageCards,
  ImageCollage,
  ImageContent,
  LogoSection,
  RichTextContent,
  ServiceCard,
  SponsorContent,
  QuickLinks,
  FoodMenu,
  BlogContent,
  DSolutionBadgeContent,
  OpeningHoursContent,
  AccordionContent,
} from '@bm-js/atlascloud-ui'
import { useEditorData } from '../../utils/hooks'
import LinkComponent from '../LinkComponent'
import CalendarContent from '../CalendarContent'
import ContactFormContent from '../ContactFormContent'
import DocumentContent from '../DocumentContent'
import FormWrapper from '../FormWrapper'
import TipsBoxContent from '../TipsBoxContent'
import YTVideoContent from '../YTVideoContent'

type SwitcherProps = {
  content: ContentType<unknown>
}

const DynamicContentSwitcher = ({ content }: SwitcherProps) => {
  const { backendResponse, workingConfig } = useEditorData()
  const sponsors = backendResponse.website.sponsors
  const blog = backendResponse.website.blogs[0] as BlogFM
  const slicedPosts = blog.posts.slice(0, 4)

  switch (content.name) {
    case 'accordion':
      return (
        <AccordionContent content={content as ContentType<AccordionData>} />
      )

    case 'blog':
      return (
        <BlogContent
          blogName={blog.viewName}
          LinkComponent={LinkComponent}
          content={content as ContentType<BlogData>}
          slicedPosts={slicedPosts}
        />
      )

    case 'header':
      return <HeaderContent content={content as ContentType<HeaderData>} />

    case 'bullets':
      return <BulletPoints content={content as ContentType<BulletsData>} />

    case 'button':
      return (
        <ButtonContent
          content={content as ContentType<ButtonData>}
          LinkComponent={LinkComponent}
        />
      )

    case 'calendar':
      return <CalendarContent content={content as ContentType<CalendarData>} />

    case 'contactform':
      return (
        <ContactFormContent
          content={content as ContentType<ContactFormData>}
          websiteConfig={workingConfig}
        />
      )

    case 'document':
      return <DocumentContent content={content as ContentType<DocumentData>} />

    case 'form':
      return <FormWrapper content={content as ContentType<FormData>} />

    case 'gallery':
      return <Gallery content={content as ContentType<GalleryData>} />

    case 'image':
      return <ImageContent content={content as ContentType<ImageData>} />

    case 'imageCards':
      return (
        <ImageCards
          content={content as ContentType<ImageCardsData>}
          LinkComponent={LinkComponent}
        />
      )

    case 'imageCollage':
      return <ImageCollage content={content as ContentType<ImageCollageData>} />

    case 'logoSection':
      return (
        <LogoSection
          content={content as ContentType<LogoSectionData>}
          LinkComponent={LinkComponent}
        />
      )

    case 'quicklinks':
      return (
        <QuickLinks
          content={content as ContentType<QuicklinksData>}
          LinkComponent={LinkComponent}
        />
      )

    case 'richtext':
      return <RichTextContent content={content as ContentType<RichTextData>} />

    case 'serviceCard':
      return (
        <ServiceCard
          content={content as ContentType<ServiceCardData>}
          LinkComponent={LinkComponent}
        />
      )

    case 'sponsors':
      return (
        <SponsorContent
          content={content as ContentType<SponsorsData>}
          sponsors={sponsors}
          LinkComponent={LinkComponent}
        />
      )

    case 'tipsbox':
      return (
        <TipsBoxContent
          content={content as ContentType<TipsBoxData>}
          websiteConfig={workingConfig}
        />
      )

    case 'ytvideo':
      return <YTVideoContent content={content as ContentType<YtVideoData>} />

    case 'dSolutionsBadge':
      return (
        <DSolutionBadgeContent
          content={content as ContentType<DSolutionsBadgeData>}
        />
      )
    case 'foodMenu':
      return <FoodMenu content={content as ContentType<FoodMenuData>} />

    case 'openingHours':
      return (
        <OpeningHoursContent
          content={content as ContentType<OpeningHoursData>}
        />
      )

    default:
      return null
  }
}

export default DynamicContentSwitcher
